// src/pages/DeveloperToolsPage.js
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeCtx } from "../App";
import { DatabaseIcon, Table2Icon, Upload, KeyRound, Settings2, ShieldAlert, FlaskConical } from "lucide-react";
import { api } from "../scripts/api";
import "../App.css";

function MaintenancePanel() {
  const [active, setActive]     = useState(false);
  const [message, setMessage]   = useState("");
  const [draft, setDraft]       = useState("");
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState("");

  const DEFAULT_MSG = "The system is currently undergoing maintenance. Please check back shortly.";

  useEffect(() => {
    api.get("/admin/maintenance")
      .then(({ data }) => {
        setActive(!!data.active);
        setMessage(data.message || "");
        setDraft(data.message || "");
      })
      .catch(() => {});
  }, []);

  const enable = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/maintenance/enable", { message: draft.trim() || DEFAULT_MSG });
      setActive(true);
      setMessage(data.message || draft.trim() || DEFAULT_MSG);
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to enable maintenance mode");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/admin/maintenance/disable");
      setActive(false);
      setMessage("");
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to disable maintenance mode");
    } finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ marginTop: 24, borderLeft: `3px solid ${active ? "var(--danger, #e05)" : "var(--card-border)"}` }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div className="row" style={{ gap: 10, alignItems: "center" }}>
          <ShieldAlert size={20} style={{ color: active ? "var(--danger, #e05)" : "var(--muted)" }} />
          <div>
            <div style={{ fontWeight: 700 }}>Maintenance Mode</div>
            <div className="muted" style={{ fontSize: "0.8rem" }}>
              {active
                ? <span style={{ color: "var(--danger, #e05)" }}>ACTIVE — all non-admin users see the lockout screen</span>
                : "Inactive — system is accessible normally"}
            </div>
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          {active
            ? <button className="btn" onClick={disable} disabled={busy}>Disable</button>
            : <button className="btn" style={{ background: "var(--danger, #c00)", borderColor: "var(--danger, #c00)" }} onClick={enable} disabled={busy}>Enable</button>
          }
        </div>
      </div>

      {!active && (
        <div style={{ marginTop: 14 }}>
          <label className="muted" style={{ fontSize: "0.8rem", display: "block", marginBottom: 4 }}>
            Message shown to users (leave blank for default)
          </label>
          <textarea
            className="input"
            rows={3}
            style={{ width: "100%", resize: "vertical", fontFamily: "inherit" }}
            placeholder={DEFAULT_MSG}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
        </div>
      )}

      {active && message && (
        <div className="muted" style={{ marginTop: 10, fontSize: "0.85rem", fontStyle: "italic" }}>
          "{message}"
        </div>
      )}

      {active && (
        <div style={{ marginTop: 14 }}>
          <label className="muted" style={{ fontSize: "0.8rem", display: "block", marginBottom: 4 }}>
            Update message (takes effect immediately)
          </label>
          <div className="row" style={{ gap: 8 }}>
            <textarea
              className="input"
              rows={2}
              style={{ flex: 1, resize: "vertical", fontFamily: "inherit" }}
              placeholder={DEFAULT_MSG}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <button className="btn-outline" disabled={busy} onClick={async () => {
              setBusy(true); setErr("");
              try {
                const { data } = await api.post("/admin/maintenance/enable", { message: draft.trim() || DEFAULT_MSG });
                setMessage(data.message || draft.trim() || DEFAULT_MSG);
              } catch (e) { setErr(e?.response?.data?.error || "Update failed"); }
              finally { setBusy(false); }
            }}>Update</button>
          </div>
        </div>
      )}

      {err && <div className="muted" style={{ color: "var(--danger, #e05)", marginTop: 8 }}>{err}</div>}
    </div>
  );
}

export default function DeveloperToolsPage() {
  const nav = useNavigate();
  const { theme, setTheme } = useContext(ThemeCtx);

  const tiles = [
    {
      to: "/admin/dbs",
      label: "Database Settings",
      sub: "Registry, paths, active DBs",
      icon: DatabaseIcon,
    },
    {
      to: "/admin/schema",
      label: "Schema Manager",
      sub: "Edit tables & columns",
      icon: Table2Icon,
    },
    {
      to: "/admin/import",
      label: "Inventory Importer",
      sub: "Upload spreadsheets -> tables",
      icon: Upload,
    },
    {
      to: "/admin/api-keys",
      label: "API Keys",
      sub: "Manage integrations",
      icon: KeyRound,
    },
    {
      to: "/admin/settings",
      label: "System Settings",
      sub: "Environment & feature flags",
      icon: Settings2,
    },
    {
      to: "/admin/pg-preview",
      label: "PG Preview",
      sub: "Browse PostgreSQL migration data",
      icon: FlaskConical,
    },
  ];

  return (
    <div className="container">
      <div className="splash">
        <div style={{ width: "100%" }}>
          <div
            className="row"
            style={{
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div>
              <div className="splash-title">Developer Tools</div>
              <div className="splash-sub">
                Imports • DB registry • Schema • API keys • System settings
              </div>
            </div>

            <div className="row" style={{ gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <div className="row" style={{ gap: 8, alignItems: "center" }}>
                <select
                  className="input"
                  style={{ width: 150 }}
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  title="Theme"
                >
                  <option value="system">System</option>
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </select>
              </div>
              <button className="btn-outline" onClick={() => nav("/admin")}>
                Admin
              </button>
              <button className="btn-outline" onClick={() => nav("/home")}>
                Home
              </button>
            </div>
          </div>

          <div className="scan-grid" style={{ marginTop: 16 }}>
            {tiles.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.label} className="scan-tile" onClick={() => nav(t.to)}>
                  <div className="scan-tile-top">
                    <div className="scan-icon">
                      <Icon size={22} />
                    </div>
                    <div className="scan-chev">↗</div>
                  </div>
                  <div className="scan-title">{t.label}</div>
                  <div className="scan-sub">{t.sub}</div>
                </button>
              );
            })}
          </div>

          <MaintenancePanel />

          <div className="muted" style={{ marginTop: 14 }}>
            Tip: choose the Inventory Importer to refresh tables from the latest spreadsheet before schema edits.
          </div>
        </div>
      </div>
    </div>
  );
}
